import React, { useCallback, useEffect, useRef, useState } from "react";

// Drag-to-resize split between a page's table (left) and form (right), the same
// bar Outage Management has, packaged for the Admin setup pages.
//
// `breakpoint` is the width at which the page switches from stacked to side by
// side (its container's `lg:flex-row` / `xl:flex-row`). Below it the bar is
// hidden and the panels keep their stacked layout. "always" is for a page that
// is side by side at every width.
//
// Until the bar is first dragged, `leftPct` is null and no split classes are
// applied, so each page keeps exactly the default proportions it had before.
// Double-clicking the bar returns to that default.
export type SplitBreakpoint = "lg" | "xl" | "always";

// Full class strings, not built by interpolation, so Tailwind's scanner sees them.
// `!`: the panels already carry their own grow/basis classes in the same variant
// (e.g. `lg:basis-1/3`), and Tailwind does not guarantee which of two same-variant
// basis utilities wins.
const VARIANTS: Record<SplitBreakpoint, { container: string; left: string; divider: string }> = {
  lg: {
    container: "lg:gap-0",
    // Grow 0 / shrink 1: the table takes the dragged width, but gives way if the
    // form cannot get any narrower, so the form is never clipped.
    left: "lg:!grow-0 lg:!shrink lg:!basis-[var(--split-left)] lg:min-w-0",
    divider: "hidden lg:flex",
  },
  xl: {
    container: "xl:gap-0",
    left: "xl:!grow-0 xl:!shrink xl:!basis-[var(--split-left)] xl:min-w-0",
    divider: "hidden xl:flex",
  },
  always: {
    // No gap on the container: the bar itself is the spacing between the panels.
    container: "",
    left: "!grow-0 !shrink !basis-[var(--split-left)] min-w-0",
    divider: "flex",
  },
};

export const useResizableSplit = (
  breakpoint: SplitBreakpoint,
  { min = 25, max = 75 }: { min?: number; max?: number } = {}
) => {
  const [leftPct, setLeftPct] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stopDragRef = useRef<(() => void) | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();

      const onPointerMove = (moveEvent: PointerEvent) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
        setLeftPct(Math.min(Math.max(pct, min), max));
      };

      const stopDrag = () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", stopDrag);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        stopDragRef.current = null;
      };

      stopDragRef.current = stopDrag;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", stopDrag);
    },
    [min, max]
  );

  // A tab switch can unmount the page mid-drag; don't leave the listeners or the
  // body cursor behind.
  useEffect(() => () => stopDragRef.current?.(), []);

  const variant = VARIANTS[breakpoint];
  const isSplit = leftPct !== null;

  return {
    containerRef,
    containerStyle: (isSplit
      ? { ["--split-left" as string]: `${leftPct}%` }
      : undefined) as React.CSSProperties | undefined,
    containerClassName: variant.container,
    leftClassName: isSplit ? variant.left : "",
    dividerProps: {
      className: variant.divider,
      onPointerDown,
      onDoubleClick: () => setLeftPct(null),
    },
  };
};

export const SplitDivider: React.FC<{
  className: string;
  onPointerDown: (e: React.PointerEvent) => void;
  onDoubleClick: () => void;
}> = ({ className, onPointerDown, onDoubleClick }) => (
  <div
    role="separator"
    aria-orientation="vertical"
    title="Drag to resize, double-click to reset"
    className={`${className} items-stretch justify-center flex-shrink-0 w-4 cursor-col-resize group touch-none`}
    onPointerDown={onPointerDown}
    onDoubleClick={onDoubleClick}
  >
    <div className="w-1 rounded-full bg-gray-300 group-hover:bg-blue-400 transition-colors duration-150" />
  </div>
);
